import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReflectionCategory } from "@connect/shared";
import { useApi } from "./api";

interface ReflectionRow {
  id: string;
  prompt_category: ReflectionCategory;
  prompt: string;
  body: string;
  created_at: number;
  updated_at: number;
}

interface TodayPayload {
  day_key: string;
  prompt: { category: ReflectionCategory; text: string };
  reflection: ReflectionRow | null;
  recent_theme: { category: ReflectionCategory; count: number } | null;
}

/**
 * Returns YYYY-MM-DD for the user's current local calendar day.
 * Evening reflection is anchored to the day the user is closing, not
 * server-side UTC.
 */
export function localDayKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function useTodayReflection() {
  const api = useApi();
  const qc = useQueryClient();
  const dayKey = localDayKey();

  const query = useQuery({
    queryKey: ["reflection-today", dayKey],
    queryFn: () =>
      api.get<TodayPayload>(`/reflections/today?day=${encodeURIComponent(dayKey)}`),
  });

  const save = useMutation({
    mutationFn: (body: string) =>
      api.post("/reflections", {
        day_key: dayKey,
        prompt_category: query.data?.prompt.category,
        prompt: query.data?.prompt.text,
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reflection-today", dayKey] });
    },
  });

  return { query, save, dayKey };
}
